import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { BureauCheckItemDTO, BureauMomentDTO, BureauTodayDTO } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { compressPhoto, useBureauCheck, useBureauReport } from '../../api/bureau.js';

const TAG: Record<BureauMomentDTO['status'], string> = {
  done: 'Fait', now: 'Maintenant', late: 'En retard', todo: 'À venir', off: 'Repos',
};
const REPORT_KINDS = ['Espace pas nettoyé', 'Consommable manquant', 'Matériel cassé', 'Autre'] as const;

export function JourneeView({ today }: { today: BureauTodayDTO }) {
  const check = useBureauCheck(today.date);
  const report = useBureauReport();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [photoFor, setPhotoFor] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [reportFor, setReportFor] = useState<BureauMomentDTO | null>(null);
  const [reportNote, setReportNote] = useState('');

  const onItem = (it: BureauCheckItemDTO) => {
    if (it.autoKey) {
      if (!it.done) navigate('/entry');
      else toast.info(it.autoDetail ?? 'Point coché depuis la Saisie.');
      return;
    }
    if (!it.done && it.photoRequired) {
      setPhotoFor(it.id);
      fileRef.current?.click();
      return;
    }
    check.mutate({ itemId: it.id, done: !it.done }, { onError: () => toast.danger('Le point n’a pas pu être enregistré.') });
  };

  const onPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !photoFor) return;
    const blob = await compressPhoto(file);
    check.mutate({ itemId: photoFor, done: true, photo: blob }, {
      onSuccess: () => toast.success('Photo jointe, point validé'),
      onError: () => toast.danger('La photo n’a pas pu être envoyée.'),
    });
    setPhotoFor(null);
  };

  if (!today.workday) {
    return <div className="card bx-empty">Pas de routine aujourd’hui : jour non ouvré.</div>;
  }

  return (
    <div className="bx-moments">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} data-testid="bx-photo-input" />
      {today.moments.map((m) => {
        const doneN = m.items.filter((i) => i.done).length;
        const collapsed = m.status === 'done' && !expanded[m.id];
        return (
          <section key={m.id} className={`card bx-moment st-${m.status}`} data-testid={`bx-moment-${m.id}`}>
            <header className="bx-moment-head">
              <span className="bx-mono bx-moment-time">{m.time}</span>
              <h3 className="bx-moment-title">{m.label}</h3>
              <span className={`bx-tag st-${m.status}`}>{TAG[m.status]}</span>
            </header>
            {collapsed ? (
              <button type="button" className="bx-moment-summary" onClick={() => setExpanded((x) => ({ ...x, [m.id]: true }))}>
                {doneN}/{m.items.length} faits · afficher le détail
              </button>
            ) : (
              <>
                {m.items.map((it) => (
                  <button
                    type="button" key={it.id}
                    className={it.done ? 'bx-item is-done' : 'bx-item'}
                    onClick={() => onItem(it)}
                    data-testid={`bx-item-${it.id}`}
                  >
                    <span className="bx-check" aria-hidden="true">{it.done ? '✓' : ''}</span>
                    <span className="bx-item-main">
                      <span className="bx-item-label">{it.label}</span>
                      {it.autoKey && <span className="bx-item-meta auto">{it.autoDetail}</span>}
                      {!it.autoKey && it.done && (
                        <span className="bx-item-meta">
                          {it.doneByName?.split(' ')[0]} · {new Date(it.doneAt!).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          {it.photoLogId && (
                            <> · <a href={`/api/bureau/checks/${it.photoLogId}/photo`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>photo</a></>
                          )}
                        </span>
                      )}
                    </span>
                    {it.autoKey && <span className="bx-pill auto">Auto</span>}
                    {it.photoRequired && !it.done && <span className="bx-pill photo">Photo</span>}
                  </button>
                ))}
                <button type="button" className="bx-report-btn" onClick={() => { setReportFor(m); setReportNote(''); }}>
                  Signaler un manquement
                </button>
              </>
            )}
          </section>
        );
      })}

      {reportFor && (
        <div className="modal-backdrop" role="presentation" onClick={() => setReportFor(null)}>
          <div className="modal-card bx-sheet" role="dialog" aria-modal="true" aria-labelledby="bx-report-title" onClick={(e) => e.stopPropagation()}>
            <div className="bx-eyebrow">{reportFor.time} · {reportFor.label}</div>
            <h2 id="bx-report-title" className="bx-sheet-title">Signaler un manquement</h2>
            <p className="bx-sheet-sub">Les référents sont prévenus, et un suivi reste ouvert jusqu’à la résolution.</p>
            <label className="sr-only" htmlFor="bx-report-note">Précision</label>
            <input id="bx-report-note" className="input" placeholder="Précision (facultatif) : salle de réunion, toilettes hommes…" value={reportNote} onChange={(e) => setReportNote(e.target.value)} />
            <div className="bx-report-grid">
              {REPORT_KINDS.map((k) => (
                <button
                  type="button" key={k} className="btn"
                  onClick={() => report.mutate({ momentId: reportFor.id, kind: k, note: reportNote.trim() || undefined }, {
                    onSuccess: () => { toast.success('Signalé · suivi créé'); setReportFor(null); },
                    onError: () => toast.danger('Le signalement n’a pas pu être envoyé.'),
                  })}
                >
                  {k}
                </button>
              ))}
            </div>
            <button type="button" className="btn bx-sheet-close" onClick={() => setReportFor(null)}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
